/**
 * @file Services.tsx
 * @description Service cards grid showcasing all financial services offered.
 * 
 * Services displayed:
 * - Term Insurance
 * - Health Insurance  
 * - Life Insurance
 * - Mutual Funds
 * - General Insurance
 * - Bonds
 * - Loan Against Mutual Funds
 * - Government Schemes
 * 
 * Each card includes:
 * - Service icon
 * - Title and description
 * - Key benefit highlight
 * - Link to detailed service page
 * 
 * @dependencies React Router DOM for Link
 */
import { Link } from 'react-router-dom';

/**
 * Grid of service cards linking to individual service detail pages.
 */
export default function Services() {
  const services = [
    {
      title: 'Health Insurance',
      description: 'Comprehensive medical coverage for you and your family.',
      benefit: 'Cashless hospitalization at 10,000+ hospitals',
      link: '/services/health-insurance',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      color: 'red',
    },
    {
      title: 'Term Insurance',
      description: 'Maximum coverage at minimal cost. Protect your family\'s future.',
      benefit: 'High coverage starting at ₹500/month',
      link: '/services/term-insurance',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      color: 'blue',
    },
    {
      title: 'Mutual Funds',
      description: 'Diversified investment portfolio for wealth creation.',
      benefit: 'Professionally managed funds, tax-efficient',
      link: '/services/mutual-funds',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      color: 'green',
    },
    {
      title: 'Life Insurance',
      description: 'Long-term savings with life cover. Secure future goals.',
      benefit: 'Guaranteed returns with tax benefits',
      link: '/services/life-insurance',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      color: 'purple',
    },
    {
      title: 'General Insurance',
      description: 'Protect your assets - vehicle, home, travel, business, cyber risks, and more.',
      benefit: 'Comprehensive coverage for all your assets & liabilities',
      link: '/services/general-insurance',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      color: 'yellow',
    },
    {
      title: 'Bonds',
      description: 'Fixed-income investments with stable returns and safety.',
      benefit: 'Government-backed bonds with tax benefits',
      link: '/services/bonds',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: 'purple',
    },
    {
      title: 'Loan',
      description: 'Get instant liquidity without selling your investments.',
      benefit: 'Low interest rates, funds in 24-48 hours',
      link: '/services/mf-loan',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9" strokeWidth="2"/>
          <g fill="currentColor" stroke="none">
            <rect x="8" y="8" width="7.5" height="1.2"/>
            <rect x="8" y="10.5" width="7.5" height="1.2"/>
            <path d="M13 11.7 Q13.5 12.2 13.5 13.5 Q13.5 15 12 15.8 Q11 16.3 9.8 16.3 L9.3 16.3 L9.3 15.5 Q10.5 15.5 11.3 15 Q12.5 14.3 12.5 13.2 Q12.5 12.3 12 11.7 Z M9.3 15.5 L9 15.8 L14 20 L14.5 19.5 L10 15.2 Z"/>
          </g>
        </svg>
      ),
      color: 'indigo',
    },
    {
      title: 'Government Schemes',
      description: 'Safe investments with government guarantee - PPF, NSC, SCSS, NPS, PM-JAY & more.',
      benefit: 'Guaranteed returns with tax benefits',
      link: '/services/govt-schemes',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      color: 'yellow',
    },
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; hover: string }> = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-600', hover: 'hover:bg-blue-100' },
      red: { bg: 'bg-red-50', text: 'text-red-600', hover: 'hover:bg-red-100' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-600', hover: 'hover:bg-purple-100' },
      green: { bg: 'bg-green-50', text: 'text-green-600', hover: 'hover:bg-green-100' },
      yellow: { bg: 'bg-yellow-50', text: 'text-yellow-600', hover: 'hover:bg-yellow-100' },
      indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', hover: 'hover:bg-indigo-100' },
    };
    return colors[color];
  };

  return (
    <section id="services" className="py-16 md:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Our <span className="text-blue-600">Services</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Comprehensive financial solutions tailored to your needs and life goals
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {services.map((service, index) => {
            const colors = getColorClasses(service.color);
            return (
              <div
                key={index}
                className="group bg-white rounded-2xl p-6 shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200"
              >
                <div
                  className={`inline-flex p-4 rounded-xl ${colors.bg} ${colors.text} ${colors.hover} mb-4 group-hover:scale-110 transition-transform`}
                >
                  {service.icon}
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {service.title}
                </h3>

                <p className="text-gray-600 mb-4">{service.description}</p>

                <div className="flex items-center space-x-2 text-sm text-green-600 font-medium mb-4">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{service.benefit}</span>
                </div>

                {service.link.startsWith('#') ? (
                  <a
                    href={service.link}
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold group-hover:translate-x-1 transition-transform"
                  >
                    Learn More
                    <svg
                      className="w-5 h-5 ml-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </a>
                ) : (
                  <Link
                    to={service.link}
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold group-hover:translate-x-1 transition-transform"
                  >
                    Learn More
                    <svg
                      className="w-5 h-5 ml-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            Not sure which service you need? Let's talk!
          </p>
          <a
            href="#contact"
            className="inline-block bg-blue-600 text-white px-8 py-4 rounded-xl hover:bg-blue-700 transition-all shadow-md hover:shadow-lg font-semibold"
          >
            Get Personalized Recommendation
          </a>
        </div>
      </div>
    </section>
  );
}
